// ISDP CUSTOM: Shared permission definitions — imported by both frontend and backend
// Frontend: RequirePermission + usePermission | Backend: IsdpPermissionPolicy
import { createPermission } from '@backstage/plugin-permission-common';

export const isdpAdminPagePermission = createPermission({
  name: 'isdp.admin.page',
  attributes: { action: 'read' },
});

export const isdpLandscapePermission = createPermission({
  name: 'isdp.landscape.page',
  attributes: { action: 'read' },
});

// External tier types — simplified: all external users are "registered",
// premium access is per-product (group:default/premium-{productId})
export type ExternalTier = 'registered' | 'premium';

// Tier group constants — used in auth module and permission policy
export const TIER_GROUPS = {
  registered: 'group:default/external-registered',
} as const;

// Per-product premium groups: group:default/premium-{productId}.
//
// The set of products is DATA, not code: a product id is valid iff it is a key of the
// backend config `partnerDocs` (one repo per product). This library is shared by the
// frontend and the backend and stays config-free, so it only knows the SHAPE of an id;
// membership is validated where config is available (backend modules/partnerProducts.ts).
/** A product id — a stable URL slug that is also the entitlement key. */
export type ProductId = string;

/** The shape every product id must have (lower-case URL slug, ≤ 64 chars). Ids are URL
 *  path segments and config keys, where `.` is a path separator, so nothing else passes. */
export const PRODUCT_ID_PATTERN = /^[a-z0-9][a-z0-9-]{0,63}$/;

export function isProductIdShape(value: unknown): value is ProductId {
  return typeof value === 'string' && PRODUCT_ID_PATTERN.test(value);
}

export const PREMIUM_GROUP_PREFIX = 'group:default/premium-';

export function premiumGroup(productId: ProductId): string {
  return `${PREMIUM_GROUP_PREFIX}${productId}`;
}

/** The product ids carried as `premium-<id>` groups, in order, de-duplicated. Groups whose
 *  suffix is not a well-formed id are ignored. No hardcoded product list is consulted. */
export function productIdsFromGroups(groups: readonly string[]): ProductId[] {
  const out: ProductId[] = [];
  for (const g of groups) {
    if (typeof g !== 'string' || !g.startsWith(PREMIUM_GROUP_PREFIX)) continue;
    const id = g.slice(PREMIUM_GROUP_PREFIX.length);
    if (isProductIdShape(id) && !out.includes(id)) out.push(id);
  }
  return out;
}

/** Narrow arbitrary values to well-formed, de-duplicated product ids (drops anything else).
 *  Shape only — whether an id is a CONFIGURED product is the backend's call. */
export function toProductIds(values: readonly unknown[] | undefined | null): ProductId[] {
  if (!Array.isArray(values)) return [];
  const out: ProductId[] = [];
  for (const v of values) {
    if (isProductIdShape(v) && !out.includes(v)) out.push(v);
  }
  return out;
}
